import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "const/axios";

const TEMPLATES_BASE = "/visa-documents/templates";
const visaBase = (visaId) => `/visa-applications/${visaId}/documents`;

export const useVisaDocumentTemplates = (options = {}) => {
  return useQuery({
    queryKey: ["visaDocumentTemplates"],
    queryFn: async () => (await axios.get(TEMPLATES_BASE)).data,
    ...options,
  });
};

export const useUploadVisaDocumentTemplate = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ title, description, file }) => {
      const formData = new FormData();
      formData.append("title", title);
      if (description) formData.append("description", description);
      formData.append("file", file);
      const response = await axios.post(TEMPLATES_BASE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["visaDocumentTemplates"] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useDeleteVisaDocumentTemplate = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId) => axios.delete(`${TEMPLATES_BASE}/${templateId}`),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["visaDocumentTemplates"] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useVisaTemplateFields = (templateId, options = {}) => {
  return useQuery({
    queryKey: ["visaDocumentTemplateFields", templateId],
    queryFn: async () => (await axios.get(`${TEMPLATES_BASE}/${templateId}/fields`)).data,
    enabled: Boolean(templateId) && options.enabled !== false,
    ...options,
  });
};

export const useRenameVisaTemplateField = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, fieldId, name }) =>
      (await axios.patch(`${TEMPLATES_BASE}/${templateId}/fields/${fieldId}`, { name })).data,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["visaDocumentTemplateFields", variables?.templateId],
      });
      options.onSuccess?.(data, variables, context);
    },
    onError: options.onError,
  });
};

export const useVisaDocumentStatuses = (visaId, options = {}) => {
  return useQuery({
    queryKey: ["visaDocumentStatuses", visaId],
    queryFn: async () => (await axios.get(visaBase(visaId))).data,
    enabled: Boolean(visaId) && options.enabled !== false,
    ...options,
  });
};

/** Fetches the filled/signed PDF as a blob through the authenticated endpoint (never a bare,
 * reusable URL) and opens it in a new tab as an object URL. The tab is opened synchronously
 * (before the await) so it stays inside the click's user-activation window — opening it after
 * the fetch resolves gets silently popup-blocked in most browsers. Revokes the object URL once
 * the new tab has had a chance to load it. */
/** Saves the filled PDF to disk rather than opening it. Used beside the preview so an admin can
 * keep a copy of what the client submitted before countersigning it. */
export const downloadVisaDocumentPdf = async (visaId, templateId, fileName = "document.pdf") => {
  const response = await axios.get(`${visaBase(visaId)}/${templateId}/download`, {
    responseType: "blob",
  });
  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
};

/** Saves the filled PDF with all encryption removed, so it opens and edits like a plain file. */
export const downloadUnencryptedVisaDocumentPdf = async (visaId, templateId, title) => {
  let response;
  try {
    response = await axios.get(`${visaBase(visaId)}/${templateId}/download-unencrypted`, {
      responseType: "blob",
    });
  } catch (error) {
    throw new Error(await readBlobErrorMessage(error, "Could not prepare the unencrypted copy"));
  }
  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `${sanitizeFileName(title)}-unencrypted.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
};

const sanitizeFileName = (title) =>
  String(title || "document")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "document";

/** Fetched on open rather than kept: the preview links are presigned and expire. */
export const useVisaDocumentSignatures = (visaId, templateId, options = {}) => {
  return useQuery({
    queryKey: ["visaDocumentSignatures", visaId, templateId],
    queryFn: async () =>
      (await axios.get(`${visaBase(visaId)}/${templateId}/signatures`)).data.signatures,
    enabled: Boolean(visaId) && Boolean(templateId) && options.enabled !== false,
    refetchOnMount: "always",
    cacheTime: 0,
  });
};

export const fetchVisaSignatureImage = async (visaId, templateId, fieldName) =>
  (
    await axios.get(`${visaBase(visaId)}/${templateId}/signatures/image`, {
      params: { field: fieldName },
      responseType: "blob",
    })
  ).data;

/** earlier opens the copy kept from before the document was reissued on its original. */
export const openVisaDocumentPdf = async (visaId, templateId, { earlier = false } = {}) => {
  const newTab = window.open("", "_blank");
  try {
    const path = earlier ? "legacy-download" : "download";
    const response = await axios.get(`${visaBase(visaId)}/${templateId}/${path}`, {
      responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(response.data);
    if (newTab) {
      newTab.location.href = blobUrl;
    } else {
      window.open(blobUrl, "_blank");
    }
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
  } catch (err) {
    newTab?.close();
    throw err;
  }
};

/** Recomputed live as the admin picks a MARA agent / bank account in the send dialog — only ever
 * returns that specific pick's own still-missing details, never a static pre-picker guess. */
export const useLinkedAdminFields = (
  { visaId, templateId, maraAgentId, bankAccountId },
  options = {}
) => {
  return useQuery({
    queryKey: [
      "visaDocumentLinkedFields",
      visaId,
      templateId,
      maraAgentId || null,
      bankAccountId || null,
    ],
    queryFn: async () =>
      (
        await axios.get(`${visaBase(visaId)}/${templateId}/linked-fields`, {
          params: {
            maraAgentId: maraAgentId || undefined,
            bankAccountId: bankAccountId || undefined,
          },
        })
      ).data,
    ...options,
  });
};

/**
 * Renders the document exactly as the send would, from the dialog's current answers — nothing is
 * saved and no email goes out. Returns a blob URL the caller owns and must revoke.
 */
export const previewVisaDocument = async ({
  visaId,
  templateId,
  maraAgentId,
  bankAccountId,
  fieldValues,
  uppercaseValues,
}) => {
  try {
    const response = await axios.post(
      `${visaBase(visaId)}/${templateId}/preview`,
      { maraAgentId, bankAccountId, fieldValues, uppercaseValues },
      { responseType: "blob" }
    );
    return window.URL.createObjectURL(response.data);
  } catch (error) {
    // responseType blob applies to errors too, so the server's "Please fill in: …" arrives as a
    // Blob rather than parsed JSON. Read it back out or the admin just sees a generic failure.
    throw new Error(await readBlobErrorMessage(error));
  }
};

const readBlobErrorMessage = async (error, fallback = "Could not build the preview") => {
  const data = error?.response?.data;
  if (!(data instanceof Blob)) {
    return error?.response?.data?.errorMessage || error?.response?.data?.message || fallback;
  }
  try {
    const parsed = JSON.parse(await data.text());
    return parsed?.errorMessage || parsed?.message || fallback;
  } catch {
    return fallback;
  }
};

export const useSendVisaDocument = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      visaId,
      templateId,
      message,
      maraAgentId,
      bankAccountId,
      fromEmail,
      fieldValues,
      uppercaseValues,
    }) =>
      (
        await axios.post(`${visaBase(visaId)}/${templateId}/send`, {
          message,
          maraAgentId,
          bankAccountId,
          fromEmail,
          fieldValues,
          uppercaseValues,
        })
      ).data,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["visaDocumentStatuses", variables?.visaId] });
      // Signing invites/OTPs/completions are now logged to email_send_log with the clientId,
      // so refresh the Client Emails tab as well.
      queryClient.invalidateQueries({ queryKey: ["client-email-logs"] });
      options.onSuccess?.(data, variables, context);
    },
    onError: options.onError,
  });
};

/**
 * Withdraws the signing link for a document the client has not submitted yet. The server clears the
 * packet's public token, so the URL already sitting in the client's inbox stops resolving.
 */
export const useRevokeVisaDocument = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ visaId, templateId }) =>
      (await axios.post(`${visaBase(visaId)}/${templateId}/revoke`)).data,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["visaDocumentStatuses", variables?.visaId] });
      options.onSuccess?.(data, variables, context);
    },
    onError: options.onError,
  });
};

export const useCompleteVisaDocument = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ visaId, templateId, fieldValues }) =>
      (await axios.post(`${visaBase(visaId)}/${templateId}/complete-admin`, { fieldValues })).data,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["visaDocumentStatuses", variables?.visaId] });
      options.onSuccess?.(data, variables, context);
    },
    onError: options.onError,
  });
};
